// Developer: Ankit Gupta
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Project } from '@/types';

// ─── App-wide Zustand store ───────────────────────────────────────────────────

interface AppState {
  // ── Navigation ──────────────────────────────────────────────────────────────
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebar: () => void;

  // ── Active project ───────────────────────────────────────────────────────────
  currentProject: Project | null;
  setCurrentProject: (project: Project | null) => void;
  activeProjectId: number | null;
  setActiveProjectId: (id: number | null) => void;

  // ── Instance info (loaded from server config) ─────────────────────────────
  instanceName: string;
  setInstanceName: (name: string) => void;
}

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      // ── Navigation ────────────────────────────────────────────────────────────
      sidebarCollapsed: false,
      setSidebarCollapsed: (collapsed) =>
        set({ sidebarCollapsed: collapsed }),
      toggleSidebar: () =>
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

      // ── Active project ─────────────────────────────────────────────────────────
      currentProject: null,
      setCurrentProject: (project) =>
        set({
          currentProject: project,
          activeProjectId: project?.id ?? null,
        }),
      activeProjectId: null,
      setActiveProjectId: (id) => {
        if (id) {
          try {
            localStorage.setItem('dp.dashboard.project', String(id));
          } catch {
            // ignore storage errors
          }
        }
        set({ activeProjectId: id });
      },

      // ── Instance info ──────────────────────────────────────────────────────────
      instanceName: 'DataPilot',
      setInstanceName: (name) => set({ instanceName: name }),
    }),
    {
      name: 'datapilot-app-store',
      // Persist UI preferences and active project choice across page loads
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        activeProjectId: state.activeProjectId,
        instanceName: state.instanceName,
      }),
    },
  ),
);

// ─── Convenience selectors ────────────────────────────────────────────────────

export const selectCurrentProject = (state: AppState) => state.currentProject;
export const selectActiveProjectId = (state: AppState) => state.activeProjectId;
export const selectSidebarCollapsed = (state: AppState) =>
  state.sidebarCollapsed;

